import { Button, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import {
  pipesAndSpanHeadersUnit,
  fetchCellValue,
  generateUniqueHeaders,
  getEquipmentColumnsFromAssemblyTemplate,
} from '@/utils';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ExportCsvSubheading } from '../InsightsExplorer/ExportCsvSubheading';
import {
  useGetAssembliesByTemplateFieldsFiltersForEquipmentExportLazyQuery,
  useGetStructureNameLazyQuery,
  Assembly,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useEquipmentData } from '@/hooks/useEquipmentData';
import { useRouter } from '@/hooks';
import { format } from 'date-fns';
import { Columns, PipesAndSpanColumns } from '@/types';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import ExcelJS from 'exceljs';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { formatEnumForDisplay } from '@/utils';

// Extended types for POI export functionality
interface PoiCount {
  poiType: string;
  count: number;
}

interface AssemblyWithPoiCounts extends Assembly {
  poiCounts?: PoiCount[];
}

interface ColumnDataWithPoi {
  key: string;
  label: string;
  active: boolean;
  poiTemplateName?: string;
}

const staticColumnHeaders = [
  {
    key: 'tagName',
    columnName: 'Component',
    visibility: true,
    isSortable: false,
  },
] as Columns[];

export const EquipmentExportXlsx = ({ title = 'Export Excel' }: { title?: string }) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const selectedTemplateId = useSelectedAssemblyTemplateId()!;
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const allSelectedPaintRegionIds = useAtomValue(state.allSelectedPaintRegionIds);
  const selectedDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const selectedPoiTypes = useAtomValue(state.selectedFilteredPoiTypes);
  const structureLocations = useAtomValue(state.structureLocations);
  const selectedSavedBlock = useAtomValue(customBlockState.selectedSavedBlock);
  const structureId = useGetStructureId();
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const unitSystem = useAtomValue(state.unitSystem);
  const { userColumns, refetchUserPreferences } = useUserPreferences(
    structureId,
    'COMPONENT',
    'xlsx'
  );
  const { getCellValue } = useEquipmentData();
  const { getUnitSuffix } = useUnitConversion();
  const { showSnackBar } = useSnackBarMessage({});

  const router = useRouter();
  const { inspection } = router.query;

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForEquipmentExportLazyQuery({
      variables: {
        input: {
          selectedTemplateFilters,
          structureId,
          templateId: selectedTemplateId,
          selectedPaintRegionIds: [
            ...allSelectedPaintRegionIds,
            ...(selectedSavedBlock?.paintRegion?.id ? [selectedSavedBlock.paintRegion.id] : []),
          ],
          selectedDeckIds,
          selectedWorkpackIds,
          ...(selectedPoiTypes.length > 0 && { selectedPoiTypes }),
        },
        pageInfo: {
          sortBy: 'createdAt',
          sortDir: 1,
        },
      },
    });

  const [getStructureName] = useGetStructureNameLazyQuery({
    variables: { structureId: (inspection as string) || '' },
    fetchPolicy: 'cache-first',
  });

  const templateColumns = useMemo(() => {
    if (!assemblyTemplate) return [];
    return getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
  }, [assemblyTemplate]);

  // Get POI templates for export columns
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const poiTemplateColumns = useMemo(() => {
    if (!allPointOfInterestTemplates || allPointOfInterestTemplates.length === 0) {
      return [];
    }

    return allPointOfInterestTemplates.map((template) => ({
      key: `poi_${template?.key?.toLowerCase()}`,
      name: formatEnumForDisplay(template?.name),
      isSelected: userColumns.includes(formatEnumForDisplay(template?.name)),
      displayUnit: '',
      unit: '',
      templateFieldKey: '',
      displayConfig: { type: 'POI_COUNT' },
      poiTemplateName: template?.name,
    }));
  }, [allPointOfInterestTemplates, userColumns]);

  const dialogColumns = useMemo(() => {
    const updatedColumns = [...userColumns];

    if (!updatedColumns.includes('Component')) {
      updatedColumns.push('Component');
    }

    const selectableColumns = [...staticColumnHeaders, ...templateColumns].map(
      ({ columnName, key, displayUnit, unit, templateFieldKey, displayConfig }) => ({
        key,
        name: columnName,
        isSelected: updatedColumns.includes(key),
        displayUnit: displayUnit || '',
        unit: unit || '',
        templateFieldKey,
        displayConfig,
      })
    );

    return [
      ...selectableColumns,
      // Add single Points of Interests entry in modal (generates all POI template columns when selected)
      {
        key: 'pointsOfInterests',
        name: 'Points of Interests',
        isSelected: updatedColumns.includes('pointsOfInterests'),
        displayUnit: '',
        unit: '',
        templateFieldKey: '',
        displayConfig: { type: 'POI_TEMPLATES' },
      },
      {
        key: '3dViewURL',
        name: '3D View URL',
        isSelected: updatedColumns.includes('3dViewURL'),
        displayUnit: '',
        unit: '',
        templateFieldKey: '',
        displayConfig: undefined,
      },
      {
        key: 'platformName',
        name: 'Platform Name',
        isSelected: updatedColumns.includes('platformName'),
        displayUnit: '',
        unit: '',
        templateFieldKey: '',
        displayConfig: undefined,
      },
    ];
  }, [templateColumns, userColumns]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = () => {
    refetchUserPreferences();
    setDialogOpen(true);
  };

  const foundViewpoint = useCallback(
    (key: string) => {
      return structureLocations?.find((location) => location.id === key)?.name ?? 'N/A';
    },
    [structureLocations]
  );

  const handleExportXlsx = useCallback(
    async (selectedColumnsKeys: string[]) => {
      try {
        const [structureNameQueryData, assembliesData] = await Promise.all([
          getStructureName(),
          fetchFilteredAssemblies(),
        ]);
        const structureName = structureNameQueryData?.data?.structure?.name;
        const assemblies = assembliesData?.data?.getAssembliesByTemplateFieldsFilters ?? [];
        // Create a workbook
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Assemblies');

        // Prepare column headers
        const headers: string[] = [];
        const columnData: ColumnDataWithPoi[] = [];

        // Add static columns
        staticColumnHeaders.forEach(({ columnName, key }) => {
          if (selectedColumnsKeys.includes(key)) {
            headers.push(columnName);
            columnData.push({ key, label: columnName, active: true });
          }
        });

        // Add dynamic columns
        for (const column of templateColumns) {
          const { columnName, key, unit, displayUnit, templateFieldKey } = column;
          if (
            ['mounted_pipes', 'child_components', 'span_distance'].some((k) =>
              templateFieldKey?.includes(k)
            )
          ) {
            continue;
          }
          if (selectedColumnsKeys.includes(key)) {
            const unitSuffix = getUnitSuffix({
              unit: displayUnit ?? unit,
              field: templateFieldKey,
            });
            const label = unitSuffix ? `${columnName} (${unitSuffix})` : columnName;
            headers.push(label);
            columnData.push({ key, label, active: true });
          }
        }

        // Add POI template columns if Points of Interests is selected
        if (selectedColumnsKeys.includes('pointsOfInterests')) {
          poiTemplateColumns.forEach((column) => {
            headers.push(column.name);
            columnData.push({
              key: column.key,
              label: column.name,
              active: true,
              poiTemplateName: column.poiTemplateName,
            });
          });
        }

        // Add 3D View URL column if selected
        if (selectedColumnsKeys.includes('3dViewURL')) {
          headers.push('3D View URL');
          columnData.push({ key: '3dViewURL', label: '3D View URL', active: true });
        }

        // Add Platform Name column if selected
        if (selectedColumnsKeys.includes('platformName')) {
          headers.push('Platform Name');
          columnData.push({ key: 'platformName', label: 'Platform Name', active: true });
        }

        const fieldKeysToCheck = ['mounted_pipes', 'child_components', 'span_distance'] as const;

        const pipesAndSpanMeta: Record<
          'mounted_pipes' | 'child_components' | 'span_distance',
          { headers: PipesAndSpanColumns[]; maxRows: number }
        > = {
          mounted_pipes: { headers: [], maxRows: 0 },
          child_components: { headers: [], maxRows: 0 },
          span_distance: { headers: [], maxRows: 0 },
        };

        const selectedKey = fieldKeysToCheck.filter((fk) => selectedColumnsKeys.includes(fk));
        const foundHeaders = new Set<string>();
        let foundRows = false;
        for (const assembly of assemblies) {
          const templateData = assembly?.templateFieldData || {};
          for (const key of selectedKey) {
            const fieldData = templateData[key] || {};
            const fieldMetaData = assemblyTemplate?.fields.find(
              (field) => field.key === key
            )?.metaData;
            const fieldHeaders = Array.isArray(fieldMetaData?.headers ?? fieldData.headers)
              ? (fieldMetaData?.headers ?? fieldData.headers)
              : [];
            const rows = Array.isArray(fieldData.rows) ? fieldData.rows : [];

            if (fieldHeaders.length > 0) {
              generateUniqueHeaders(pipesAndSpanMeta[key].headers, fieldHeaders);
            }

            if (rows.length > 0) {
              pipesAndSpanMeta[key].maxRows = Math.max(pipesAndSpanMeta[key].maxRows, rows.length);
              foundRows = true;
            }

            if (pipesAndSpanMeta[key].headers.length > 0) {
              foundHeaders.add(key);
            }
          }

          if (
            selectedKey.length > 0 &&
            selectedKey.every((k) => foundHeaders.has(k) && foundRows)
          ) {
            break;
          }
        }
        // add numbering to each header according to rows length (size 1 , size 2)
        for (const key of fieldKeysToCheck) {
          if (!selectedColumnsKeys.includes(key)) continue;
          const pipesAndSpanData = pipesAndSpanMeta[key];
          if (!pipesAndSpanData.headers.length || pipesAndSpanData.maxRows === 0) continue;
          for (let index = 0; index < pipesAndSpanData.maxRows; index++) {
            for (const header of pipesAndSpanData.headers) {
              const unit = header.displayUnit
                ? ` (${pipesAndSpanHeadersUnit(getUnitSuffix, header.displayUnit)})`
                : '';
              const indexSuffix = pipesAndSpanData.maxRows > 1 ? `${index + 1}` : '';
              headers.push(`${header.displayName} ${indexSuffix}${unit}`);
            }
          }
        }

        // Add header row
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, name: 'Arial' };
          cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        });

        // Add data rows
        assemblies.forEach((assembly) => {
          const rowData: (string | number)[] = [];
          const assemblyWithPoi = assembly as AssemblyWithPoiCounts;

          columnData.forEach(({ key, active }) => {
            if (!active) return;

            // Handle static columns
            if (key === 'componentId') {
              rowData.push(assembly?.id ?? '');
              return;
            }
            if (key === 'tagName') {
              rowData.push(assembly?.tagName ?? '');
              return;
            }

            if (key === '3dViewURL') {
              const url = `${
                window.location.origin
              }/analysis/${structureId}/viewer?assembly=${encodeURIComponent(assembly.tagName)}`;
              rowData.push(url);
              return;
            }

            if (key === 'platformName') {
              rowData.push(structureName ?? '');
              return;
            }

            // Handle POI template columns (individual columns for each POI type)
            if (key.startsWith('poi_')) {
              if (assemblyWithPoi.poiCounts) {
                // Extract POI type from column key (poi_blister -> BLISTER)
                const poiTypeFromKey = key.replace('poi_', '').toUpperCase();
                const poiCount = assemblyWithPoi.poiCounts.find(
                  (poi: PoiCount) => poi.poiType.toUpperCase() === poiTypeFromKey
                );
                rowData.push(poiCount?.count ?? 0);
              } else {
                rowData.push(0);
              }
              return;
            }

            // Handle dynamic columns
            const column = templateColumns.find((col) => col.key === key);
            if (column) {
              const { displayConfig, columnName } = column;
              const { value, subValue } = getCellValue(assembly as Assembly, column, true);

              // getCellValue flattens arrays to a newline-joined string when
              // exportXlsx=true, but the inferred return type still admits arrays;
              // narrow defensively so downstream string ops don't blow up.
              const flatValue = Array.isArray(value) ? value.join('\n') : value;
              let cellValue = subValue ? `${flatValue} (${subValue})` : flatValue;

              // Handle special cases
              if (displayConfig?.type === 'LINK' && cellValue !== 'N/A') {
                cellValue = window.location.origin + cellValue;
              } else if (columnName === 'Governing Blister Viewpoint' && cellValue !== 'N/A') {
                cellValue = foundViewpoint(cellValue);
              }

              // For Excel calculations, preserve numeric values

              const excelValue =
                cellValue !== 'N/A' && cellValue !== null && !isNaN(Number(cellValue))
                  ? Number(cellValue)
                  : cellValue;

              rowData.push(excelValue);
            }
          });

          // Generate row value for associated child and pipe support
          const templateFieldData = assembly?.templateFieldData ?? {};
          for (const key of fieldKeysToCheck) {
            if (!selectedColumnsKeys.includes(key)) continue;
            const meta = pipesAndSpanMeta[key];
            if (!meta.headers.length || meta.maxRows === 0) continue;
            const rows = (templateFieldData?.[key]?.rows ?? []) || [];
            for (let index = 0; index < meta.maxRows; index++) {
              const row = rows[index];
              for (const { key, displayUnit } of meta.headers) {
                const cellResult = row
                  ? fetchCellValue(
                      row,
                      key,
                      unitSystem,
                      structurePipesByStructureId[structureId],
                      String(displayUnit)
                    )
                  : { rawValue: 'N/A' };

                const { rawValue } = cellResult;

                rowData.push(
                  rawValue === undefined || rawValue === null
                    ? 'N/A'
                    : Number.isFinite(Number(rawValue))
                      ? Number(rawValue)
                      : String(rawValue)
                );
              }
            }
          }

          const row = worksheet.addRow(rowData);

          // Add hyperlink to the 3D View URL cell if present
          const urlIdx = columnData.findIndex((col) => col.key === '3dViewURL');
          if (urlIdx !== -1 && rowData[urlIdx]) {
            row.getCell(urlIdx + 1).value = { text: '3D View', hyperlink: String(rowData[urlIdx]) };
            row.getCell(urlIdx + 1).font = {
              color: { argb: 'FFFF0000' },
              underline: true,
              name: 'Arial',
            };
          }

          row.eachCell((cell) => {
            cell.font = { name: 'Arial' };
            cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
          });
          row.height = 20;
        });

        // Auto-size columns
        worksheet.columns.forEach((column, index) => {
          const headerWidth = (headers[index]?.toString() || '').length;
          const maxDataWidth = Math.max(
            ...assemblies.map((_, rowIndex) => {
              const cell = worksheet.getCell(rowIndex + 2, index + 1);
              return (cell.value?.toString() || '').length;
            })
          );
          const columnWidth = Math.min(Math.max(headerWidth, maxDataWidth, 10) + 2, 50);
          column.width = columnWidth;
        });

        // Generate filename with date
        const date = format(new Date(), 'dd-MMM-yyyy');
        const fileName = `${structureName}-${date}.xlsx`;

        // Generate and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();

        window.URL.revokeObjectURL(url);

        showSnackBar('Query result has been exported successfully', 'success');
      } catch (error) {
        console.error('Export error:', error);
        showSnackBar('Failed to export Excel', 'error');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchFilteredAssemblies,
      foundViewpoint,
      getCellValue,
      getStructureName,
      getUnitSuffix,
      showSnackBar,
      structureId,
      templateColumns,
      poiTemplateColumns,
      structurePipesByStructureId,
      unitSystem,
    ]
  );

  return (
    <Stack>
      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Customize Table Columns"
        subHeading={<ExportCsvSubheading />}
        applyButtonText="Download"
        applyButtonLoadingText="Downloading..."
        successMessage="Query result has been exported successfully"
        errorMessage="Failed to export Excel"
        userPreferenceKey="COMPONENT"
        userPreferenceType="xlsx"
        onApply={handleExportXlsx}
        onClose={() => setDialogOpen(false)}
      />

      <Button
        id="export-equipment-xlsx"
        onClick={handleClick}
        startIcon={<AbyssIcon iconName="excel" style={{ height: '1.6rem', width: '1.6rem' }} />}
        variant="outlined"
        size="small"
      >
        {title}
      </Button>
    </Stack>
  );
};
